// manhole.ts

export interface ManholeDefinition {
    type: string;
    width: number;
    height: number;
    thickness: number;
    spacing: number;
    rowSpacing: number;
    rabbetWidth?: number;
    applicableToLidType: string;
    visibleToAdminOnly?: boolean;
    // Optional bracket-related properties:
    bracketDiameter?: number;
    bracketRadius?: number;
    bracketWidth?: number;
    bracketHeight?: number;
    bracketPosition?: number;
}

export const manholeDefinitions: ManholeDefinition[] = [
    {
        type: "BEDEC-100",
        width: 25,
        height: 100,
        thickness: 8,
        spacing: 0,
        rowSpacing: 0,
        rabbetWidth: 5.25,
        applicableToLidType: "A15",
    },
    {
        type: "BEDEC-80",
        width: 25,
        height: 80,
        thickness: 8,
        spacing: 0,
        rowSpacing: 0,
        rabbetWidth: 5.25,
        applicableToLidType: "A15",
    },
    {
        type: "BEDEC-60",
        width: 25,
        height: 60,
        thickness: 8,
        spacing: 0,
        rowSpacing: 0,
        rabbetWidth: 5.25,
        applicableToLidType: "A15",
    },
    {
        type: "V2A-1000",
        width: 50,
        height: 100,
        thickness: 7.5,
        spacing: 0,
        rowSpacing: 7,
        rabbetWidth: 3.5,
        applicableToLidType: "B125",
    },
    {
        type: "V2A-10",
        width: 47,
        height: 68,
        thickness: 7.5,
        spacing: 0,
        rowSpacing: 7,
        rabbetWidth: 3.5,
        applicableToLidType: "B125",
    },
    {
        type: "GBDC",
        width: 52,
        height: 100,
        thickness: 7.5,
        spacing: 0,
        rowSpacing: 0,
        rabbetWidth: 3.5,
        applicableToLidType: "D400",
    },
    {
        type: "GBDC-90",
        width: 92,
        height: 90,
        thickness: 7.5,
        spacing: 0,
        rowSpacing: 0,
        rabbetWidth: 3.5,
        applicableToLidType: "D400",
        visibleToAdminOnly: true,
    },
    {
        type: "VG",
        width: 75,
        height: 75,
        thickness: 7.5,
        spacing: 0,
        rowSpacing: 0,
        rabbetWidth: 3.5,
        applicableToLidType: "D400",
        visibleToAdminOnly: true,
    },
    {
        type: "GBE",
        width: 52,
        height: 100,
        thickness: 7.5,
        spacing: 0,
        rowSpacing: 0,
        rabbetWidth: 3.5,
        applicableToLidType: "E600",
    },
    {
        type: "GBE-90",
        width: 92,
        height: 90,
        thickness: 7.5,
        spacing: 0,
        rowSpacing: 0,
        rabbetWidth: 3.5,
        applicableToLidType: "E600",
        visibleToAdminOnly: true,
    },
    {
        type: "BET-130",
        width: 27,
        height: 130,
        thickness: 10,
        spacing: 0,
        rowSpacing: 0,
        applicableToLidType: "subfloor",
        bracketDiameter: 1.5,
        bracketRadius: 3,
        bracketWidth: 20,
        bracketHeight: 6.25,
        bracketPosition: 8.5,
    },
    {
        type: "BET-150",
        width: 27,
        height: 150,
        thickness: 10,
        spacing: 0,
        rowSpacing: 0,
        applicableToLidType: "subfloor",
        bracketDiameter: 1.5,
        bracketRadius: 3,
        bracketWidth: 20,
        bracketHeight: 6.25,
        bracketPosition: 8.5,
    },
];

/**
 * Returns the closure sizes for the given manhole type by filtering the definitions.
 */
export const getClosureSizes = (
    manholeType: string
): ManholeDefinition =>
    manholeDefinitions.filter((m) => m.type === manholeType)[0];

/**
 * Returns intersection positions between closures along a side.
 * @param manholeSideLength - The side length of the manhole.
 * @param closureSideLength - The side length of the closure element.
 * @returns An array of numbers denoting intersection positions.
 */
export const getClosuresIntersectionPositions = (
    manholeSideLength: number,
    closureSideLength: number
): number[] => {
    const intersectionCount = Math.floor(
        (manholeSideLength + 0.1) / closureSideLength - 1
    );
    return Array.from({length: intersectionCount}, (_, i) => (i + 1) * closureSideLength);
};

export const getShortLabelText = (manholeType: string): string => {
    return manholeType === "GBDC-90" ? "GBDC"
        : manholeType === "GBE-90" ? "GBE"
            : manholeType;
};

const FULL_LABEL_BY_TYPE: Record<string, string> = {
    "BEDEC-100": "BEDEC-100/A15",
    "BEDEC-80": "BEDEC-80/A15",
    "BEDEC-60": "BEDEC-60/A15",
    "V2A-1000": "V2A-1000/B125",
    "V2A-10": "V2A-10/B125",
    "GBDC": "GBDC/D400",
    "GBDC-90": "GBDC/D400",
    "VG": "VG/D400",
    "GBE": "GBE/E600",
    "GBE-90": "GBE/E600",
    "BET-130": "BET-130/Unterflur",
    "BET-150": "BET-150/Unterflur",
};

export const getFullLabelText = (manholeType: string): string => {
    return FULL_LABEL_BY_TYPE[manholeType] ?? "";
};
